import {
    List,
    ListItem,
    Typography,
    CircularProgress,
    Grid,
    Paper,
    ListItemIcon,
    Button,
    Box,
    ListItemText,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { isDataLoaded, SiteProfile } from '../../types';
import { useSiteProfileData } from '../../data/DataProviders/SiteProfileDataProvider';
import EditIcon from '@mui/icons-material/Edit';
import { useDispatch, useSelector } from 'react-redux';
import { useLanguageLocale } from '../../hooks/language-locale-hooks';
import { Selectors } from '../../selectors';
import { DeleteForever, GpsFixed, Notes } from '@mui/icons-material';
import locationStyles from '../../app/location-management/LocationStyle';
import { ConfirmationPopUp } from '../ConfirmationPopUp/ConfirmationPopUp';
import { Actions, Thunks } from '../../actions';
import { useToasts } from '../../data/DataProviders/ToastProvider';
import { useAuthHelper } from '../AuthContextProvider';
import { pushAppRoute } from '../../data/domain/route-manager';
import { useHistory } from 'react-router-dom';
import { AppRoute } from '../../routes';
import { ConnectedDeviceList } from '../ConnectedDeviceList/ConnectedDeviceList';
import { SiteDevicesDataProvider } from '../../data/DataProviders/SiteDevicesDataProvider';
import { DataProviderCache } from '../../data/DataProviders/DataProviderCache';
import { StatusTag } from '../StatusTag/StatusTag';
import * as PXBColors from '@brightlayer-ui/colors';
import { LabelWithSubTitle } from '../LabelWithSubTitle/LabelWithSubtitle';
import { NavigationHeader } from '../NavigationHeader/NavigationHeader';
import { useCurrentAppRoute } from '../../hooks/app-route-hooks';
import { SignalRSiteDataProvider } from '../../data/DataProviders/SignalRSiteDataProvider';
import { DeviceMasterProvider } from '../../data/DataProviders/DeviceMasterProvider';

export const SiteProfileData: React.FC<{ appRoute: AppRoute; data: SiteProfile }> = (props): JSX.Element => {
    const siteProfileData = useSiteProfileData();
    const history = useHistory();
    const classes = locationStyles();

    const currentAppRoute = useCurrentAppRoute();
    const dispatch = useDispatch();
    const siteState = useSelector(Selectors.Site.site);
    const { t } = useLanguageLocale();
    const { addToast } = useToasts();
    const authHelper = useAuthHelper();
    const [openDelete, setOpenDelete] = React.useState(false);
    const [siteData, setSiteData] = useState<SiteProfile | null>(null);

    useEffect(() => {
        if (props.data) setSiteData(props.data);
    }, [props.data]);

    useEffect(() => {
        if (!siteState.deleteSiteLoading && siteState.deleteSiteSuccess && isDataLoaded(siteProfileData)) {
            addToast('Site deleted successfully.');
            dispatch(Thunks.Dashboard.locationRefresh(true));
            pushAppRoute(history, props.appRoute);
            dispatch(Actions.Site.SiteDeleteReset());
        }
    }, [siteState.deleteSiteLoading, siteState.deleteSiteSuccess]); //eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!siteState.loading && siteState.success && siteData) {
            const updateSite: SiteProfile = { ...siteData };
            updateSite.name = siteState.siteName;
            setSiteData(updateSite);
            dispatch(Actions.Site.SiteUpdateReset());
        }
    }, [siteState.loading, siteState.success]); //eslint-disable-line react-hooks/exhaustive-deps

    const closePopUp = (): void => {
        setOpenDelete(false);
    };
    const deleteSite = (): void => {
        if (isDataLoaded(siteProfileData)) {
            dispatch(Thunks.Site.deleteSite(siteProfileData.value.id, addToast, authHelper));
            setOpenDelete(false);
        }
    };
    const navigateToEditSite = (): void => {
        if (currentAppRoute.type === 'SiteProfile') {
            pushAppRoute(history, {
                type: 'EditSite',
                orgId: currentAppRoute.orgId,
                locationId: currentAppRoute.locationId,
                siteId: currentAppRoute.siteId,
            });
        }
    };

    return (
        <>
            {siteData && (
                <>
                    <NavigationHeader
                        locationName={''}
                        siteName={siteData.name}
                        for={'site'}
                        connectedDevice=""
                        device={''}
                    />
                    <Paper>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={8}>
                                <Box>
                                    <List>
                                        <ListItem
                                            alignItems="flex-start"
                                            style={{ paddingLeft: 24 }}
                                            data-testid="site-name"
                                        >
                                            <ListItemText>
                                                <Typography className={classes.wrapText} variant="h6">
                                                    {siteData.name}
                                                </Typography>
                                            </ListItemText>

                                            <ListItemIcon style={{ display: 'inline' }} data-testid="site-status">
                                                <StatusTag style={{ marginRight: 25 }} status={siteData.status || ''} />
                                            </ListItemIcon>
                                        </ListItem>

                                        <ListItem data-testid="gps-coordinates" className={classes.margin8}>
                                            <ListItemIcon>
                                                <GpsFixed />
                                            </ListItemIcon>
                                            {siteData.longitude === '' && siteData.latitude === '' ? (
                                                <Typography variant="caption" style={{ fontStyle: 'italic' }}>
                                                    No GPS Coordinates are available
                                                </Typography>
                                            ) : (
                                                <LabelWithSubTitle
                                                    title={
                                                        siteData.longitude !== ''
                                                            ? `${siteData.latitude} /${siteData.longitude}`
                                                            : siteData.latitude
                                                    }
                                                    subTitle={'GPS Coordinates'}
                                                />
                                            )}
                                        </ListItem>

                                        <ListItem
                                            style={{ width: '70%' }}
                                            data-testid="site-notes"
                                            className={classes.margin8}
                                        >
                                            <ListItemIcon>
                                                <Notes />
                                            </ListItemIcon>
                                            {siteData.description === '' ? (
                                                <Typography variant="caption" style={{ fontStyle: 'italic' }}>
                                                    No Notes are available
                                                </Typography>
                                            ) : (
                                                <div>
                                                    <Typography
                                                        component="pre"
                                                        variant="body2"
                                                        display="inline"
                                                        style={{
                                                            lineHeight: 1.25,
                                                            fontWeight: 600,
                                                            whiteSpace: 'pre-wrap',
                                                        }}
                                                    >
                                                        {siteData.description}
                                                    </Typography>
                                                </div>
                                            )}
                                        </ListItem>
                                    </List>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Box style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <List>
                                        <ListItem data-testid="device-details">
                                            <Button
                                                style={{ marginRight: 15, width: 300 }}
                                                variant="contained"
                                                color="primary"
                                                startIcon={<EditIcon />}
                                                data-testid="edit-action"
                                                onClick={(): void => {
                                                    navigateToEditSite();
                                                }}
                                            >
                                                Edit Site
                                            </Button>
                                        </ListItem>
                                        <ListItem>
                                            <Button
                                                style={{
                                                    width: 300,
                                                    color: PXBColors.red[500],
                                                    borderColor: PXBColors.gray[100],
                                                }}
                                                variant="outlined"
                                                onClick={(): void => setOpenDelete(true)}
                                                startIcon={
                                                    siteState.deleteSiteLoading && !siteState.deleteSiteSuccess ? (
                                                        <CircularProgress
                                                            style={{ color: PXBColors.red[500] }}
                                                            size="2rem"
                                                        />
                                                    ) : (
                                                        <DeleteForever />
                                                    )
                                                }
                                                color="inherit"
                                                data-testid="delete-action"
                                            >
                                                Delete Forever
                                            </Button>
                                        </ListItem>
                                    </List>
                                </Box>
                            </Grid>
                        </Grid>
                    </Paper>

                    {currentAppRoute.type === 'SiteProfile' && (
                        <DataProviderCache for={'gateway-devices'}>
                            <SiteDevicesDataProvider siteId={siteData.id} recursive={false} includeflag={true}>
                                <DeviceMasterProvider>
                                    <SignalRSiteDataProvider
                                        siteId={siteData.id}
                                        locationId={currentAppRoute.locationId}
                                    >
                                        <ConnectedDeviceList siteId={siteData.id} />
                                    </SignalRSiteDataProvider>
                                </DeviceMasterProvider>
                            </SiteDevicesDataProvider>
                        </DataProviderCache>
                    )}
                </>
            )}
            {openDelete && (
                <ConfirmationPopUp
                    title={'Delete This Site?'}
                    body={
                        <Typography variant="subtitle2" style={{ marginBottom: 24 }}>
                            This Site and all of its data will be deleted forever. Do you wish to proceed?
                        </Typography>
                    }
                    okButtonText={'Delete Forever'}
                    cancelButtonText={t('ACTIONS.CANCEL')}
                    show={openDelete}
                    closeDialog={closePopUp}
                    handleOk={(): void => {
                        deleteSite();
                    }}
                    backgroundColor={PXBColors.red[500]}
                    width="xs"
                    okButtonShow={true}
                    cancelButtonsShow={true}
                ></ConfirmationPopUp>
            )}
        </>
    );
};
